import React, {useState, useEffect} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    HStack,
    Input,
    Tooltip,
    useNumberInput,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

const QuantityPicker = ({
    maxOrderQuantity,
    onChange,
    value,
    productName,
    variant = 'round',
    ...props
}) => {
    const intl = useIntl()
    const isCartPage = typeof window !== 'undefined' && window.location.pathname.includes('/cart')
    const isWishlistPage =
        typeof window !== 'undefined' && window.location.pathname.includes('/wishlist')

    // Use maxOrderQuantity from props, or fall back to a default max value
    const maxQuantity = maxOrderQuantity || 999

    // State to control tooltip visibility
    const [showMaxTooltip, setShowMaxTooltip] = useState(false)

    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        value: value,
        max: maxQuantity, // Respect maxOrderQuantity limit
        min: 1,
        // Defaults
        focusInputOnChange: false,
        onChange: (valueAsString, valueAsNumber) => {
            // Show tooltip when max is reached, hide when reduced
            if (maxOrderQuantity) {
                if (valueAsNumber >= maxOrderQuantity) {
                    setShowMaxTooltip(true)
                } else {
                    setShowMaxTooltip(false)
                }
            }

            // Call the parent's onChange function with both string and number values
            // to maintain compatibility with your SelectBox implementation
            if (onChange) {
                onChange(valueAsString, valueAsNumber)
            }
        },
        onFocus: (e) => {
            // eslint-disable-next-line react/prop-types
            const {onFocus} = props

            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
            // new quantity with one click.
            e.target.select()

            // If there is a `onFocus` property define, call it with the event captured.
            // eslint-disable-next-line react/prop-types
            onFocus?.call(this, e)
        }
    })

    const inc = getIncrementButtonProps({
        variant: 'outline',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment'
            },
            {productName: productName || 'product'}
        )
    })

    const dec = getDecrementButtonProps({
        variant: 'outline',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement'
            },
            {productName: productName || 'product'}
        )
    })

    const input = getInputProps({
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity'
        })
    })

    // Auto-hide tooltip after 3 seconds when max is reached
    useEffect(() => {
        if (showMaxTooltip) {
            const timer = setTimeout(() => {
                setShowMaxTooltip(false)
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [showMaxTooltip])

    // Accessibility improvements:
    // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
    inc.tabIndex = ''
    dec.tabIndex = ''
    // 2. Allow Space or Enter key to trigger buttons
    // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
    // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
    // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }
    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }
    const styles = useMultiStyleConfig('QuantityPickerStyles', {variant})

    return (
        <Box
            data-testid={'change-quantity'}
            className={
                isCartPage || isWishlistPage ? 'quantity-picker' : 'quantity-picker-dropdown'
            }
            position="relative"
            w={{base: '100%', sm: 'auto'}}
        >
            <HStack __css={styles.container} gap={0}>
                <Button {...dec} sx={styles.button}>
                    <FormattedMessage
                        id="product_view.label.quantity_decrement"
                        defaultMessage={'-'}
                    />
                </Button>

                <Tooltip
                    label={`Maximum ${maxOrderQuantity} per customer`}
                    placement="top"
                    hasArrow
                    bg="white"
                    color={'textColor'}
                    fontSize="md"
                    px={3}
                    py={2}
                    borderRadius="md"
                    boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                    border="1px solid black"
                    isOpen={showMaxTooltip}
                    closeOnClick={false}
                    closeOnMouseDown={false}
                    offset={[0, 4.5]}
                    sx={{
                        '--tooltip-bg': 'white',
                        '--tooltip-border': '1px solid #000',
                        '& .chakra-tooltip__arrow': {
                            background: 'white',
                            borderRight: '1px solid #000',
                            borderBottom: '1px solid #000',
                            top: '1px !important'
                        }
                    }}
                >
                    <Input {...input} sx={styles.input} value={value} />
                </Tooltip>

                <Box
                    style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0
                    }}
                    aria-live="assertive"
                >
                    {showMaxTooltip ? `Maximum ${maxOrderQuantity} per customer` : ''}
                </Box>

                <Button {...inc} sx={styles.button}>
                    <FormattedMessage
                        id="product_view.label.quantity_increment"
                        defaultMessage="+"
                    />
                </Button>
            </HStack>
        </Box>
    )
}

QuantityPicker.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.number,
    maxOrderQuantity: PropTypes.number,
    maxWidth: PropTypes.string,
    productName: PropTypes.string,
    variant: PropTypes.oneOf(['round', 'circle'])
}

export default QuantityPicker
